import { FC, MouseEvent, useState } from 'react';
import {
  ClickAwayListener,
  Divider,
  Link,
  Popper,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import NonHistoricalData, { NonHistoricalDataProps } from './NonHistoricalData';
import { MdSecurity as SecurityIcon } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import { AppPages } from '../../../common/routing/app-navigator';
import { DiscoveredPolicies } from '../../../executive-summary/executive-summary-types';

const NonHistoricalDataPopper: FC<
  React.PropsWithChildren<NonHistoricalDataPopperProps>
> = ({ orgScanInfo, discoveredPolicies }) => {
  const { classes, cx } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'non-historical-data-pop-over' : undefined;

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div>
        <div
          aria-describedby={id}
          onClick={handleClick}
          className={classes.policies}>
          <SecurityIcon fontSize='small' />
          <Typography
            textTransform='capitalize'
            display='inline-flex'
            alignItems='center'
            variant='body2'
            noWrap>
            {discoveredPolicies.enabled} running policies by OX
          </Typography>
        </div>
        <Popper
          placement='bottom'
          className={classes.popper}
          id={id}
          open={open}
          anchorEl={anchorEl}>
          <div role='rowheader' className={cx(classes.row, classes.header)}>
            <Typography variant='body1' color='text.primary'>
              {discoveredPolicies.enabled} running policies by OX
            </Typography>
          </div>
          <Divider />
          <div role='row' className={classes.row}>
            <NonHistoricalData
              discoveredPolicies={discoveredPolicies}
              orgScanInfo={orgScanInfo}
            />
            <Link
              className={classes.systemLink}
              component={RouterLink}
              to={AppPages.Connectors}>
              <Typography variant='body2' color='primary'>
                Connect more Systems
              </Typography>
            </Link>
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  popper: {
    zIndex: theme.zIndex.drawer + 2,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows['6'],
    backgroundColor: theme.palette.background.paper,
  },
  systemLink: {
    paddingInline: theme.spacing(2),
    textTransform: 'capitalize',
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  policies: {
    alignItems: 'center',
    height: '2.1rem',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius,
    paddingBlock: theme.spacing(0.5),
    paddingInline: theme.spacing(1),
    border: `1px solid ${theme.palette.text.secondary}`,
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    paddingBlock: theme.spacing(1.5),
    paddingInline: theme.spacing(2),
  },
  closeButton: {
    alignSelf: 'end',
  },
  header: {
    paddingInlineStart: theme.spacing(3.5),
  },
}));

export interface NonHistoricalDataPopperProps extends NonHistoricalDataProps {
  activePoliciesCount: number;
  discoveredPolicies: DiscoveredPolicies;
}

export default NonHistoricalDataPopper;
